import { Dispatch, memo, useMemo, useRef } from 'react';

import clsx from 'clsx';
import { isWeekend } from 'date-fns';

import useHandleTimeSlotChange from '@calendar/hooks/useHandleTimeSlotChange';
import useTimetablesStore from '@calendar/store/useTimetablesStore';
import useEventTypesStore from '@event/store/useEventTypesStore';

import SelectedTimeslotModal from './SelectedTimeSlotModal';

interface HourProps {
  date: Date;
  startTimeOfHoveredTimeslot?: Date;
  isDayWithSelectedInterval: boolean;
  isDividedByTwo: boolean;
  isHovered?: boolean;
  isInMiddleOfSelectedInterval?: boolean;
  handleMouseEnter: (date: Date) => void;
  handleMouseLeave: () => void;
  isSelectedTimeSlotModalOpen: boolean | string;
  setIsSelectedTimeSlotModalOpen: Dispatch<boolean | string>;
}

const Hour = ({
  date,
  startTimeOfHoveredTimeslot,
  isDayWithSelectedInterval,
  isDividedByTwo,
  isInMiddleOfSelectedInterval,
  isHovered,
  handleMouseEnter,
  handleMouseLeave,
  isSelectedTimeSlotModalOpen,
  setIsSelectedTimeSlotModalOpen,
}: HourProps) => {
  const { timetables } = useTimetablesStore();
  const { selectedEventTimeslotLength } = useEventTypesStore();
  const handleTimeSlotChange = useHandleTimeSlotChange();
  const timerRef = useRef<NodeJS.Timeout | undefined>();

  const backgroundColor = useMemo(() => {
    if (isHovered) return 'bg-brand-primary-200';
    if (isDayWithSelectedInterval) return 'bg-brand-primary-400';
    if (isWeekend(date)) return 'bg-brand-gray-400';

    return 'bg-brand-gray-500';
  }, [date, isDayWithSelectedInterval, isHovered]);

  const handleMouseEnterMemoized = useMemo(
    () => handleMouseEnter,
    [handleMouseEnter],
  );

  const handleClick = () => {
    handleTimeSlotChange(date, selectedEventTimeslotLength, timetables);
    handleMouseLeave();
  };

  const setUpTimer = () => {
    timerRef.current = setTimeout(() => {
      setIsSelectedTimeSlotModalOpen(true);
    }, 500);
  };

  const cleanUpTimer = () => {
    clearTimeout(timerRef.current);
    setIsSelectedTimeSlotModalOpen(false);
  };

  return (
    <div
      className={clsx(
        'mb-1 ml-1 h-12 w-8 flex-[0_0_32px] cursor-pointer rounded transition-all duration-300 first-of-type:ml-0 motion-reduce:transition-none lg:relative',
        backgroundColor,
      )}
      key={Number(date)}
      onClick={() => handleClick()}
      onMouseEnter={() => {
        handleMouseEnterMemoized(date);
        setUpTimer();
      }}
      onMouseLeave={() => {
        handleMouseLeave();
        cleanUpTimer();
      }}
    >
      {isInMiddleOfSelectedInterval &&
        isSelectedTimeSlotModalOpen &&
        startTimeOfHoveredTimeslot && (
          <SelectedTimeslotModal
            onMouseEnter={() => {
              handleMouseLeave();
              cleanUpTimer();
            }}
            isDividedByTwo={isDividedByTwo}
            length={selectedEventTimeslotLength?.duration || 0}
            startDate={startTimeOfHoveredTimeslot}
            isTillMidnight={selectedEventTimeslotLength?.tillMidnight}
            isFullDay={selectedEventTimeslotLength?.fullDay}
            isHovered={isHovered}
          />
        )}
    </div>
  );
};

export default memo(
  Hour,
  (prevProps, nextProps) =>
    prevProps.date === nextProps.date &&
    prevProps.isDayWithSelectedInterval ===
      nextProps.isDayWithSelectedInterval &&
    prevProps.isHovered === nextProps.isHovered &&
    prevProps.isInMiddleOfSelectedInterval ===
      nextProps.isInMiddleOfSelectedInterval &&
    prevProps.isDividedByTwo === nextProps.isDividedByTwo &&
    prevProps.isSelectedTimeSlotModalOpen ===
      nextProps.isSelectedTimeSlotModalOpen,
);
